import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectSessionId,
  selectOrganizationId,
} from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";

function s3AssetUrl(base, filename) {
  if (!base || !filename) return "";
  return base + encodeURIComponent(String(filename)).replace(/%2F/g, "/");
}

const DESKTOP_BOX_W = 522;
const DESKTOP_BOX_H = 473;
const MOBILE_BOX_W = 186;
const MOBILE_BOX_H = 223;
const MAX_BOX_IMAGE_BYTES = 5 * 1024 * 1024;
const LANDING_TITLE_MAX_LEN = 15;

/**
 * Matches game.jsx `buildSlides`: each category contributes consecutive pairs
 * (items 0–1, 2–3, …). Odd leftovers in a category are unused — not global ÷ 2.
 */
function countMaxSlidePairsFromCategories(categoryImagesNames) {
  if (!categoryImagesNames) return 0;
  let cin = categoryImagesNames;
  if (typeof cin === "string") {
    try {
      cin = JSON.parse(cin);
    } catch {
      return 0;
    }
  }
  if (typeof cin !== "object" || cin === null || Array.isArray(cin)) return 0;
  return Object.keys(cin).reduce((sum, key) => {
    const arr = cin[key];
    if (!Array.isArray(arr) || arr.length < 2) return sum;
    return sum + Math.floor(arr.length / 2);
  }, 0);
}

function validateImageDimensions(file, expectedW, expectedH) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width !== expectedW || img.height !== expectedH) {
        reject(
          new Error(
            `Image must be exactly ${expectedW} × ${expectedH} px. Uploaded image is ${img.width} × ${img.height} px.`
          )
        );
      } else {
        resolve();
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image."));
    };
    img.src = url;
  });
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const organizationId = useSelector(selectOrganizationId);
  const [isIntroModelOpen, setisIntroModelOpen] = useState(false);
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  const [rules, setRules] = useState([""]);

  const [landingPageTitle, setLandingPageTitle] = useState("");
  const [thankYouPage, setThankYouPage] = useState("");
  const [thankYouGif, setThankYouGif] = useState("");
  const [isUploadingGif, setIsUploadingGif] = useState(false);
  const gifInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [desktopBox, setDesktopBox] = useState("");
  const [mobileBox, setMobileBox] = useState("");
  const [numberOfSlides, setNumberOfSlides] = useState(1);
  /** String field so empty / backspace work while typing; committed on blur & save */
  const [slidesInputStr, setSlidesInputStr] = useState("1");
  const [isUploadingDesktop, setIsUploadingDesktop] = useState(false);
  const [isUploadingMobile, setIsUploadingMobile] = useState(false);
  const [downloadingPlayData, setDownloadingPlayData] = useState(false);
  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#000000",
    item_text_color: "#1b265f",
    item_text_backgroundcolor: "#ffffff",
    rules_text_color: "#000000",
  });

  useEffect(() => {
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    if (!source) return;
    setColors((prev) => ({
      text_color: source.text_color ?? prev.text_color,
      button_color: source.button_color ?? prev.button_color,
      button_backgroundcolor: source.button_backgroundcolor ?? prev.button_backgroundcolor,
      item_text_color: source.item_text_color ?? prev.item_text_color,
      item_text_backgroundcolor:
        source.item_text_backgroundcolor ?? prev.item_text_backgroundcolor,
      rules_text_color: source.rules_text_color ?? prev.rules_text_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    // Only overwrite when the key exists on the payload. If fetchThemeData returns a
    // partial object (missing keys), do not use undefined → "" or we wipe a filename
    // just set by upload and abort the in-flight S3 image (NS_BINDING_ABORTED).
    if ("landing_page_title" in data) {
      const t = String(data.landing_page_title ?? "");
      setLandingPageTitle(t.slice(0, LANDING_TITLE_MAX_LEN));
    }
    if ("custom_text_thank_you_page" in data) {
      setThankYouPage(data.custom_text_thank_you_page ?? "");
    }
    if ("thank_you_page" in data) setThankYouGif(data.thank_you_page ?? "");
    if ("desktop_box" in data) setDesktopBox(data.desktop_box ?? "");
    if ("mobile_box" in data) setMobileBox(data.mobile_box ?? "");
    if ("number_of_slides" in data) {
      const pairCap = Math.max(
        1,
        countMaxSlidePairsFromCategories(data.category_images_names)
      );
      const n = Number(data.number_of_slides) || 1;
      const clamped = Math.min(Math.max(1, n), pairCap);
      setNumberOfSlides(clamped);
      setSlidesInputStr(String(clamped));
    }
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const S3 = process.env.REACT_APP_S3_PATH || "";

  const pairCount = countMaxSlidePairsFromCategories(data?.category_images_names);
  const maxSlides = Math.max(1, pairCount);

  useEffect(() => {
    const cap = Math.max(1, pairCount);
    setNumberOfSlides((prev) => Math.min(Math.max(1, prev), cap));
  }, [pairCount]);

  useEffect(() => {
    setSlidesInputStr(String(numberOfSlides));
  }, [numberOfSlides]);

  const commitSlidesInput = () => {
    const cap = Math.max(1, pairCount);
    let num = parseInt(slidesInputStr, 10);
    if (isNaN(num) || num < 1) num = 1;
    if (num > cap) {
      void Swal.fire(
        "Limit",
        `Number of slides cannot exceed ${cap} (total pairs available from your categories).`,
        "warning"
      );
      num = cap;
    }
    setNumberOfSlides(num);
    setSlidesInputStr(String(num));
  };

  const handleBoxImageUpload = async (
    e,
    target,
    setter,
    setUploading,
    inputRef,
    expectedW,
    expectedH
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid File", "Only JPEG / JPG / PNG files are allowed.", "error");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (file.size > MAX_BOX_IMAGE_BYTES) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    try {
      await validateImageDimensions(file, expectedW, expectedH);
    } catch (dimErr) {
      Swal.fire("Wrong Dimensions", dimErr.message || "Invalid image.", "error");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target", target);
    fd.append("themeName", String(currentTheme));
    try {
      const res = await fetch(apiUrl("/uploadSpotImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");
      setter(json.filename || "");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Uploaded", "Image updated successfully.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleGifUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "gif") {
      Swal.fire("Invalid file", "Only GIF files are allowed.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      return;
    }

    setIsUploadingGif(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target", "thank_you_page");
    fd.append("themeName", String(currentTheme));

    try {
      const res = await fetch(apiUrl("/uploadSpotImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      setThankYouGif(json.filename || "");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Uploaded", "Thank you GIF updated.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    }
    setIsUploadingGif(false);
    if (gifInputRef.current) gifInputRef.current.value = "";
  };

  const handleLandingPageTitleChange = (val) => {
    let next = val;
    if (next.length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game page title can be at most ${LANDING_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, LANDING_TITLE_MAX_LEN);
    }
    setLandingPageTitle(next);
  };

  const handleThankYouPageChange = (val) => {
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Thank you page message can be at most 300 characters. Extra text has been trimmed.",
        "warning"
      );
      val = val.slice(0, 300);
    }
    setThankYouPage(val);
  };

  const handleDownloadPlayData = async () => {
    if (!adminToken || !sessionId || !organizationId) {
      Swal.fire("Missing session", "Session or organization details are missing.", "warning");
      return;
    }
    setDownloadingPlayData(true);
    try {
      const resp = await fetch(apiUrl("/downloadPlayData"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ sessionId, organizationId }),
      });
      const contentType = resp.headers.get("content-type") || "";
      const raw = await resp.text();
      let json = null;
      if (contentType.includes("application/json")) {
        try {
          json = raw ? JSON.parse(raw) : null;
        } catch {
          json = null;
        }
      }
      if (!resp.ok || !json?.success) {
        if (!contentType.includes("application/json")) {
          throw new Error(
            "Download API is unavailable. Please restart backend server and try again."
          );
        }
        throw new Error(json?.error || "Failed to fetch play data.");
      }
      const rows = Array.isArray(json.rows) ? json.rows : [];
      if (!rows.length) {
        Swal.fire("No data", "No players found for this session yet.", "info");
        return;
      }
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [
        "name,user_ans",
        ...rows.map((r) => `${esc(r.name)},${esc(r.user_ans)}`),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `player_choices.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      Swal.fire("Downloaded", "Play data CSV downloaded successfully.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Download failed.", "error");
    }
    setDownloadingPlayData(false);
  };

  // --- submit all ---
  const handleSubmit = async () => {
    commitSlidesInput();
    if (String(landingPageTitle ?? "").length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Invalid title",
        `Game page title must be at most ${LANDING_TITLE_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const trimmedRules = rules.map((r) => String(r ?? "").trim());
    if (trimmedRules.some((r) => r === "")) {
      Swal.fire(
        "Incomplete rules",
        "Please add text to every rule, or remove empty rows using the × button before saving.",
        "warning"
      );
      return;
    }
    if (trimmedRules.length === 0) {
      Swal.fire(
        "Rules required",
        "Please add at least one rule with text before saving.",
        "warning"
      );
      return;
    }

    const payload = {
      data: {
        rules: trimmedRules,
        landing_page_title: String(landingPageTitle ?? "").slice(0, LANDING_TITLE_MAX_LEN),
        custom_text_thank_you_page: thankYouPage,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        item_text_color: colors.item_text_color,
        item_text_backgroundcolor: colors.item_text_backgroundcolor,
        rules_text_color: colors.rules_text_color,
        desktop_box: desktopBox,
        mobile_box: mobileBox,
        number_of_slides: numberOfSlides,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/customize-spot"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />


          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
                </div>
                <div className="form-group-v1">
                  <label>Game page title</label>
                  <textarea
                    value={landingPageTitle}
                    onChange={(e) => handleLandingPageTitleChange(e.target.value)}
                    placeholder="Game page title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={LANDING_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {LANDING_TITLE_MAX_LEN} characters.
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Thank you page message</label>
                  <textarea
                    value={thankYouPage}
                    onChange={(e) => handleThankYouPageChange(e.target.value)}
                    placeholder="Thank you page message"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={300}
                  />
                  <div className="code">* Maximum 300 characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Thank You GIF File</label>
                  {thankYouGif && (
                    <div className="thankyou-gif-preview">
                      <img
                        src={s3AssetUrl(S3, thankYouGif)}
                        alt="Thank you"
                        className="thankyou-gif-thumb"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <input
                    ref={gifInputRef}
                    type="file"
                    accept=".gif"
                    onChange={handleGifUpload}
                    disabled={isUploadingGif}
                    className="thankyou-gif-input"
                  />
                  {isUploadingGif && (
                    <div className="code">Uploading…</div>
                  )}
                  <div className="code">* Accepts GIF only. Max 5 MB.</div>
                </div>

                {/* Desktop Box Image */}
                <div className="form-group-v1">
                  <label>Desktop Box Image</label>
                  {desktopBox && (
                    <div className="box-img-preview">
                      <img
                        src={s3AssetUrl(S3, desktopBox)}
                        alt="Desktop box"
                        className="box-img-thumb desktop-box-thumb"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <input
                    ref={desktopInputRef}
                    type="file"
                    accept=".jpeg,.jpg,.png"
                    onChange={(e) =>
                      handleBoxImageUpload(
                        e,
                        "desktop_box",
                        setDesktopBox,
                        setIsUploadingDesktop,
                        desktopInputRef,
                        DESKTOP_BOX_W,
                        DESKTOP_BOX_H
                      )
                    }
                    disabled={isUploadingDesktop}
                    className="thankyou-gif-input"
                  />
                  {isUploadingDesktop && <div className="code">Uploading…</div>}
                  <div className="code">
                    * Accepts JPEG / JPG / PNG only.<br/>* Must be exactly {DESKTOP_BOX_W} × {DESKTOP_BOX_H} px.<br/>* Max 5 MB.
                  </div>
                </div>

                {/* Mobile Box Image */}
                <div className="form-group-v1">
                  <label>Mobile Box Image</label>
                  {mobileBox && (
                    <div className="box-img-preview">
                      <img
                        src={s3AssetUrl(S3, mobileBox)}
                        alt="Mobile box"
                        className="box-img-thumb mobile-box-thumb"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <input
                    ref={mobileInputRef}
                    type="file"
                    accept=".jpeg,.jpg,.png"
                    onChange={(e) =>
                      handleBoxImageUpload(
                        e,
                        "mobile_box",
                        setMobileBox,
                        setIsUploadingMobile,
                        mobileInputRef,
                        MOBILE_BOX_W,
                        MOBILE_BOX_H
                      )
                    }
                    disabled={isUploadingMobile}
                    className="thankyou-gif-input"
                  />
                  {isUploadingMobile && <div className="code">Uploading…</div>}
                  <div className="code">
                    * Accepts JPEG / JPG / PNG only. <br/>* Must be exactly {MOBILE_BOX_W} × {MOBILE_BOX_H} px.<br/>* Max 5 MB.
                  </div>
                </div>

                {/* Number of Slides */}
                <div className="form-group-v1">
                  <label>Number of Slides</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={slidesInputStr}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") {
                        setSlidesInputStr("");
                        return;
                      }
                      if (!/^\d+$/.test(v)) return;
                      setSlidesInputStr(v);
                    }}
                    onBlur={commitSlidesInput}
                    className="slides-number-input"
                    aria-label="Number of slides"
                  />
                  <div className="code">
                    * Max <strong>{maxSlides}</strong> slides — Pair sums per category (1–2, 3–4, …); unpaired items are ignored.
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      style={{ color: colors.rules_text_color }}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 6 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.item_text_color}
                          onChange={handleColorChange("item_text_color")}
                        />
                        <label>Item Text Color (Text type)</label>
                      </div>
                      <div className="color-code">{colors.item_text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.item_text_backgroundcolor}
                          onChange={handleColorChange("item_text_backgroundcolor")}
                        />
                        <label>Item Background Color (Text type)</label>
                      </div>
                      <div className="color-code">{colors.item_text_backgroundcolor}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.rules_text_color}
                          onChange={handleColorChange("rules_text_color")}
                        />
                        <label>Rules Text Color</label>
                      </div>
                      <div className="color-code">{colors.rules_text_color}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-download"></i> &nbsp;Download Participant List
                </div>
                <div className="form-group-v1">
                  <label>Export current session play answers</label>
                  <button
                    className="save-and-continue"
                    onClick={handleDownloadPlayData}
                    disabled={downloadingPlayData}
                  >
                    {downloadingPlayData ? "Downloading..." : "Download Participant List"}
                  </button>
                  <div className="code">
                    * Download CSV with <strong>name</strong> and <strong>user_ans</strong> for
                    current session and organization.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
